import { sleep } from '../util/sleep';
import { getLogger } from '../logger';
import { timed } from '../util/timed';
import omit from 'lodash/omit.js';
import random from 'lodash/random.js';

/**
 * Wrapper around fetch which retries the request up to 10 attempts, with exponential backoff. If
 * keepalive was enabled, it will disable keepalive if the request fails due to being too large for
 * keepalive.
 *
 * @param url The request url
 * @param fetchArgs Unlike fetch, the init parameter may be a callback returning the desired init
 *   argument. This allows the caller to adjust the argument (e.g. updating authorization header)
 *   based on a failed response. This can be done by leveraging the additional `onStatus` callback
 *   which can be provided in the init args.
 * @internal
 */
export async function fetchRetry(
  url: string,
  fetchArgs: (() => FetchRetryArgs) | FetchRetryArgs = {}
): Promise<Response | null> {
  let attempt = 1;
  let disableKeepalive = false;

  do {
    //
    // Build args for fetch from the function provided
    //

    // Override keepalive (if it was enabled) if we want to disable it explicitly.
    const args = typeof fetchArgs === 'function' ? fetchArgs() : fetchArgs;
    if (disableKeepalive) {
      args.keepalive = false;
    }

    // Add a timeout signal if one was not specified
    // note: setTimeout return type inconsistent between Node & browser so get the implicit return type
    let timeoutId: ReturnType<typeof setTimeout> | undefined = undefined;
    if (!args.signal) {
      const controller = new AbortController();
      timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      args.signal = controller.signal;
    }

    try {
      //
      // Attempt to make request
      //

      const response = await timed(
        () => fetch(url, omit(args, ['onStatus'])),
        'network_request_latency_ms'
      );

      // Run any response handler
      await args?.onStatus?.(response.status);

      // On success, log metric and stop here
      if (response.ok) {
        return response;
      }

      log.warn('Request failed', response.status, response.statusText);
    } catch (e) {
      // Fetch itself failed with an error (usually before making the request)
      log.warn('fetch error', e);

      if (args.keepalive) {
        // Keep-alive only works for events under a certain size (nominally 64kb).
        // It will immediately fail with a TypeError in other cases. To be defensive again either other browsers throwing
        // a different error or something like an ad-blocker blocking the request because it looked suspicious with keepalive,
        // retry once in all cases.
        log.debug('Received an error while keepalive was enabled, retrying without keepalive', e);
        disableKeepalive = true;
      }
    } finally {
      // Always clear the abort controller timeout from the last request
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }

    //
    // If we're here, the last request failed either by error or bad response
    //

    // Retry after backing off
    if (attempt < maxAttempts) {
      const sleepMs = calculateBackoff(attempt);
      log.debug(`Retrying after ${sleepMs}ms...`);
      await sleep(sleepMs);
    }
  } while (attempt++ < maxAttempts);

  return null;
}

type FetchRetryArgs = RequestInit & {
  onStatus?: (status: number) => Promise<void>;
};

const log = getLogger('fetchRetry');

// Calculate backoff. Retries the first attempt immediately to resolve any auth or keepalive issue.
const calculateBackoff = (attempt: number) =>
  attempt === 1 ? 0 : random(0, retryDelayBaseMs * 2 ** (attempt - 2));

// Retries
const maxAttempts = 10;
const retryDelayBaseMs = 250;

// Timeout
// taken from Android: https://github.sc-corp.net/Snapchat/android/blob/cbc496ea545a735811b273852e50fef8ebc4541f/snapchat/platform/analytics/blizzard/lib/src/main/java/com/snap/blizzard/config/BlizzardConfigurationKey.java#L127
// note: this seems too large. revisit.
const timeoutMs = 150 * 1000;
